import { useDeferredValue, useState } from 'react'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import { resolveDateFilter } from './filters/dateRange'
import FilterUpgradeTooltip from './filters/FilterUpgradeTooltip'
import { filtersToParams } from './filters/params'
import TeamFilter from './filters/TeamFilter'
import { DEFAULT_FILTERS, type UsageFiltersState } from './filters/types'
import UsageFilters from './filters/UsageFilters'
import AutoFormattingChart from './AutoFormattingChart'
import DesktopAppsChart from './DesktopAppsChart'
import DictionaryChart from './DictionaryChart'
import ErrorCard from './ErrorCard'
import ExportDialog from './ExportDialog'
import FeaturesUsedChart from './FeaturesUsedChart'
import { buildMockDesktopAppsResponse, buildMockImpactResponse, buildMockTeamMembersResponse } from './mockUpgradeData'
import MoneySavedCard from './MoneySavedCard'
import PromptQualityCard from './PromptQualityCard'
import { SkeletonCard } from './SkeletonCard'
import TeamMembersChart from './TeamMembersChart'
import TeamMembersTab from './TeamMembersTab'
import TimeSavedCard from './TimeSavedCard'
import UpgradeOverlay, { UpgradeCta } from './UpgradeOverlay'
import { useUtcDayIndex } from './useUtcDayClock'
import WordsDictatedChart from './WordsDictatedChart'

import { getDesktopApps, getImpact, getTeamMembers, getWordsDictated, type TeamMembersMode } from '@/api/teamInsights'
import { getEnterpriseMembersCount } from '@/api/teamMembersTable'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAccessKey } from '@/hooks/useAccess'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import { formatScaleCount } from '@/lib/utils'
import { selectCurrentEnterprise, selectPlanType } from '@/store/features/user/userSlice'
import { SettingType } from '@/types'
import { EventName } from '@/utils/eventDefinitions'
import { isLowerTierPlan } from '@/utils/subscription'

// Pick the right card state for a query: error → loading → success.
function QueryCard<T>({
  query,
  children,
}: {
  query: UseQueryResult<T>
  children: (data: T) => React.ReactNode
}): React.ReactNode {
  if (query.isError) return <ErrorCard onRetry={() => void query.refetch()} />
  if (query.isLoading) return <SkeletonCard />
  if (query.data === undefined) return null
  return children(query.data)
}

type TabValue = 'overview' | 'impact' | 'team-members'

const VALID_TABS: readonly TabValue[] = ['overview', 'impact', 'team-members'] as const

const isTabValue = (v: string | null): v is TabValue => VALID_TABS.includes(v as TabValue)

// Compact only above 100k so we don't squish small org numbers (e.g. 42).
const formatTabCount = (n: number) => (n > 100_000 ? formatScaleCount(n) : n.toLocaleString())

const UsageV2Page = () => {
  // Tab is URL-synced via ``?tab=`` so the member-detail page's "Back to Team
  // members" link can deep-link to the right tab. Default to overview when
  // absent / unknown.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: TabValue = isTabValue(tabParam) ? tabParam : 'overview'
  const setActiveTab = (next: TabValue) => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev)
        if (next === 'overview') updated.delete('tab')
        else updated.set('tab', next)
        return updated
      },
      { replace: true }
    )
  }
  const [filters, setFilters] = useState<UsageFiltersState>(DEFAULT_FILTERS)
  const [teamMembersMode, setTeamMembersMode] = useState<TeamMembersMode>('weekly')
  const enterprise = useSelector(selectCurrentEnterprise)
  const hasDepts = useHasDepartments()
  const enterpriseId = enterprise?.id ?? null
  const planType = useSelector(selectPlanType)
  const insightsAccess = useAccessKey('access.team-insights')
  // Until the key loads, show neither the upgrade overlay nor gated data: either would flash for some admins.
  const isLockPending = insightsAccess.status === 'pending'
  const isUpgradeLocked =
    insightsAccess.status === 'ready'
      ? !insightsAccess.enabled
      : insightsAccess.status === 'fallback' && isLowerTierPlan(planType)
  const canLoadGatedData = !isUpgradeLocked && !isLockPending
  const canExportUsage = enterprise?.settings?.[SettingType.usageExportAllowed] !== false
  const roiEnabled = useFeatureFlagEnabled('team-insights-roi')
  const { trackEvent } = useAnalytics()

  // All time resolves to no date bounds, so only this day key moves its query at midnight.
  const utcDay = useUtcDayIndex()
  const params = filtersToParams(filters)
  const resolvedRange = resolveDateFilter(filters.date)
  // Debounce rapid filter clicks (multi-select checkboxes) so we don't
  // fire a query per checkbox toggle. useDeferredValue waits for React to
  // settle before passing the new value through.
  const deferredParams = useDeferredValue(params)

  // Words Dictated renders above the upgrade overlay, so it fetches even when
  // the page is locked. Team members + desktop apps are mocked when locked.
  const wordsDictated = useQuery({
    queryKey: ['team-insights', 'words-dictated', enterpriseId, deferredParams, utcDay],
    queryFn: () => getWordsDictated(enterpriseId!, deferredParams).then((r) => r.data),
    enabled: activeTab === 'overview' && !!enterpriseId,
  })
  const teamMembers = useQuery({
    queryKey: ['team-insights', 'team-members', enterpriseId, deferredParams, teamMembersMode, utcDay],
    queryFn: () => getTeamMembers(enterpriseId!, deferredParams, teamMembersMode).then((r) => r.data),
    enabled: activeTab === 'overview' && !!enterpriseId && canLoadGatedData,
  })
  const desktopApps = useQuery({
    queryKey: ['team-insights', 'desktop-apps', enterpriseId, deferredParams, utcDay],
    queryFn: () => getDesktopApps(enterpriseId!, deferredParams).then((r) => r.data),
    enabled: activeTab === 'overview' && !!enterpriseId && canLoadGatedData,
  })
  const impact = useQuery({
    queryKey: ['team-insights', 'impact', enterpriseId, filters.teamId, utcDay],
    queryFn: () => getImpact(enterpriseId!, { team_id: filters.teamId ?? undefined }).then((r) => r.data),
    enabled: activeTab === 'impact' && !!enterpriseId && canLoadGatedData,
  })

  // Tab-label denominator. Always fetched (regardless of active tab) so the
  // Team Members label doesn't pop in after the user clicks the tab.
  const enterpriseMembersCount = useQuery({
    queryKey: ['team-members', 'enterprise-count', enterpriseId],
    queryFn: () => getEnterpriseMembersCount().then((r) => r.data),
    enabled: !!enterpriseId && canLoadGatedData,
    staleTime: 5 * 60 * 1000,
  })

  const impactSkeleton = (
    <div className="flex flex-col gap-6">
      <SkeletonCard />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SkeletonCard />
        <SkeletonCard />
      </div>
      <SkeletonCard />
      {roiEnabled ? (
        <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : (
        <SkeletonCard />
      )}
    </div>
  )

  return (
    <div className="relative flex h-full w-full flex-col gap-8 overflow-y-auto rounded-xl bg-white px-20 py-12">
      <div className="flex flex-col gap-3">
        <h6 className="pl-1.5">Usage</h6>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="gap-8">
          <TabsList variant="line">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="impact">Impact</TabsTrigger>
            <TabsTrigger value="team-members">
              {hasDepts ? 'Members' : 'Team members'}
              {canLoadGatedData && enterpriseMembersCount.data
                ? ` (${formatTabCount(enterpriseMembersCount.data.total)})`
                : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-4">
              <UsageFilters
                enterpriseId={enterpriseId}
                value={filters}
                onChange={setFilters}
                upgradeLocked={isUpgradeLocked}
              />
              {canExportUsage && (
                <FilterUpgradeTooltip active={isUpgradeLocked} align="end">
                  <ExportDialog
                    enterpriseId={enterpriseId}
                    initialFilters={filters}
                    disabled={!canLoadGatedData || !enterpriseId}
                    onExport={() => trackEvent?.(EventName.TeamInsightsExported, { tab: 'overview' })}
                  />
                </FilterUpgradeTooltip>
              )}
            </div>
            <QueryCard query={wordsDictated}>
              {(data) => (
                <WordsDictatedChart
                  response={data}
                  dataUpdatedAt={wordsDictated.dataUpdatedAt}
                  filterStart={resolvedRange.start}
                  filterEnd={resolvedRange.end}
                  dateFilter={filters.date}
                />
              )}
            </QueryCard>
            {isUpgradeLocked ? (
              <>
                <UpgradeOverlay active>
                  <TeamMembersChart
                    response={buildMockTeamMembersResponse(teamMembersMode)}
                    mode={teamMembersMode}
                    onModeChange={setTeamMembersMode}
                    dateFilter={filters.date}
                    filterStart={resolvedRange.start}
                    filterEnd={resolvedRange.end}
                    hideModeSelect
                    contentOverlay={<UpgradeCta />}
                  />
                </UpgradeOverlay>
                <UpgradeOverlay active>
                  <DesktopAppsChart response={buildMockDesktopAppsResponse()} dateFilter={filters.date} lockedPreview />
                </UpgradeOverlay>
              </>
            ) : isLockPending ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : (
              <>
                <QueryCard query={teamMembers}>
                  {(data) => (
                    <TeamMembersChart
                      response={data}
                      mode={teamMembersMode}
                      onModeChange={setTeamMembersMode}
                      dateFilter={filters.date}
                      filterStart={resolvedRange.start}
                      filterEnd={resolvedRange.end}
                    />
                  )}
                </QueryCard>
                <QueryCard query={desktopApps}>
                  {(data) => <DesktopAppsChart response={data} dateFilter={filters.date} />}
                </QueryCard>
              </>
            )}
          </TabsContent>

          <TabsContent value="impact" className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
              <FilterUpgradeTooltip active={isUpgradeLocked}>
                <TeamFilter
                  value={filters.teamId}
                  onChange={(teamId) => setFilters({ ...filters, teamId })}
                  disabled={isUpgradeLocked}
                />
              </FilterUpgradeTooltip>
            </div>
            {isUpgradeLocked ? (
              (() => {
                const mockImpact = buildMockImpactResponse()
                return (
                  <div className="flex flex-col gap-6">
                    <UpgradeOverlay active blurTarget="content" showCta hideHeaderExtras>
                      <div className="flex flex-col gap-6">
                        <TimeSavedCard response={mockImpact.minutes_saved} />
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                          <DictionaryChart response={mockImpact.dictionary} />
                          <AutoFormattingChart response={mockImpact.auto_formatting} />
                        </div>
                        <FeaturesUsedChart features={mockImpact.features_utilized} />
                      </div>
                    </UpgradeOverlay>
                    <UpgradeOverlay active blurTarget="content" hideHeaderExtras>
                      {roiEnabled ? (
                        <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
                          <PromptQualityCard response={mockImpact.ai_prompt_quality} />
                          <MoneySavedCard valueCalculator={mockImpact.value_calculator} />
                        </div>
                      ) : (
                        <PromptQualityCard response={mockImpact.ai_prompt_quality} />
                      )}
                    </UpgradeOverlay>
                  </div>
                )
              })()
            ) : isLockPending ? (
              impactSkeleton
            ) : impact.isError ? (
              <ErrorCard onRetry={() => void impact.refetch()} />
            ) : impact.isLoading || !impact.data ? (
              impactSkeleton
            ) : (
              <div className="flex flex-col gap-6">
                <TimeSavedCard response={impact.data.minutes_saved} />
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <DictionaryChart response={impact.data.dictionary} />
                  <AutoFormattingChart response={impact.data.auto_formatting} />
                </div>
                <FeaturesUsedChart features={impact.data.features_utilized} />
                {roiEnabled ? (
                  <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
                    <PromptQualityCard response={impact.data.ai_prompt_quality} />
                    <MoneySavedCard valueCalculator={impact.data.value_calculator} />
                  </div>
                ) : (
                  <PromptQualityCard response={impact.data.ai_prompt_quality} />
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="team-members" className="flex flex-col gap-6">
            {isLockPending ? <SkeletonCard /> : <TeamMembersTab upgradeLocked={isUpgradeLocked} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default UsageV2Page
